import type { LoginProtocol, SpaceUser, TokenProtocol } from '@lighthouse/core'
import { type BrowsePath, type ScanStatus, tokenStorage } from '@lighthouse/shared'

import type { HttpResp } from '@/http'
import http from '@/http'

import { JsonContentType } from './constant'
import type { PromoInviteResult, updatePasswordPayload, WechatResult } from './types'

/**
 * 登录
 * @param payload
 * @returns
 */
export async function login(payload: LoginProtocol): Promise<boolean | undefined> {
    const res = await http.post<unknown, HttpResp<TokenProtocol>>('api/auth/v1/login', payload)
    if (res?.success) {
        const { accessToken, refreshToken } = res.content
        tokenStorage.setRefreshToken(refreshToken)
        tokenStorage.setAccessToken(accessToken)
    }
    return res?.success
}

/**
 * 退出登录
 * @param
 * @returns
 */
export async function logout(refreshToken: string): Promise<boolean> {
    const res = await http.get<unknown, HttpResp>('api/v1/user/logout', {
        headers: {
            'B-Refresh-Token': refreshToken
        }
    })
    return res.success
}

/**
 *
 *  完善用户信息
 * @param {string} username
 * @param {string} password
 * @return {*}
 */
export async function consummateInfo(username: string, password: string, spaceName: string, spaceIcon?: string): Promise<boolean> {
    const res = await http.post<unknown, HttpResp<boolean>>('api/v1/user/consummate', { username, password, spaceName, spaceIcon })
    return res.success
}

/**
 *
 * 修改密码
 * @param {ResetPasswordProtocol} params
 * @return {*}
 */
export async function updatePassword(payload: updatePasswordPayload): Promise<boolean> {
    const res = await http.post<unknown, HttpResp<boolean>>('api/v1/user/updatePassword', payload)
    return res.success
}

/**
 * 用户信息
 * @param
 * @returns
 */
export async function getUser(): Promise<SpaceUser> {
    const res = await http.get<unknown, HttpResp<SpaceUser>>('api/v1/user/getCurrentUser')
    return res.content
}

/**
 * 更新用户信息
 * @param {SpaceUser} payload
 * @returns
 */
export async function updateUser(payload: SpaceUser): Promise<boolean> {
    const res = await http.post<unknown, HttpResp<boolean>>(
        'api/v1/user/updateUser',
        { ...payload },
        {
            headers: { 'Content-Type': 'application/json' }
        }
    )
    return res.content
}

export async function getBrowse() {
    const res = await http.get<unknown, HttpResp<BrowsePath>>('api/v1/user/getBrowse')
    return res?.content || {}
}

// export async function profileComplete() {
//     const res = await http.get<unknown, HttpResp<BrowsePath>>('api/v1/user/profileComplete')
//     return res?.content || {}
// }

/**
 *
 * 获取指定用户头像和昵称
 * @param
 * @return {*}
 */
export async function getUserAvatar(payload: { mobile: string; abort?: AbortController }): Promise<SpaceUser> {
    const { mobile, abort } = payload
    const res = await http.get<unknown, HttpResp<SpaceUser>>('api/v1/user/getUser', {
        params: { mobile },
        signal: abort?.signal
    })
    return res.content
}

/**
 *
 * 新用户信息收集
 * @param spaceId
 * @param teamId
 * @param parentId
 * @return {*}
 */
export async function userInfoCollect(params: Record<string, string | string[] | undefined>): Promise<boolean> {
    const res = await http.post<unknown, HttpResp>('api/v1/user/userInfoCollect', params, {
        headers: JsonContentType
    })
    return res.success
}

/**
 * 获取微信二维码
 * @param payload
 * @returns
 */
export async function getLoginQR(): Promise<WechatResult> {
    const res = await http.get<unknown, HttpResp<WechatResult>>('api/auth/v1/getLoginQr')
    return res.content
}

/**
 * 获取微信扫码结果
 * @param payload
 * @returns
 */
export async function getLoginPoll(mark: string): Promise<ScanStatus> {
    const res = await http.get<unknown, HttpResp<ScanStatus>>('api/auth/v1/poll', { params: { mark } })
    return res.content
}

/**
 *
 * 用户绑定微信
 * @param mark
 * @return {*}
 */
export async function bindWechat(mark: string): Promise<boolean> {
    const res = await http.get<unknown, HttpResp>('api/v1/user/bindWechat', {
        params: { mark }
    })
    return res.success
}

/**
 *
 * 用户解除绑定微信
 * @return {*}
 */
export async function removeBindWechat(): Promise<boolean> {
    const res = await http.get<unknown, HttpResp>('api/v1/user/removeBindWechat')
    return res.success
}

/**
 *
 * 咨询报价
 * @return {*}
 */
export async function getUserConsult(): Promise<boolean> {
    const res = await http.get<unknown, HttpResp<boolean>>('api/v1/user/consult')
    return res.success
}

/**
 * 获取API KEY
 * @return {*}
 */
export async function generateApiToken(spaceId: string): Promise<string> {
    const res = await http.get<unknown, HttpResp<string>>('/api/v1/user/generateApiToken', {
        params: { spaceId }
    })
    return res.content
}

/**
 * 获取优惠邀请人员及最高优惠用户数
 * @return {*}
 */
export async function getPromoInviteUsers(): Promise<PromoInviteResult> {
    const res = await http.get<unknown, HttpResp<PromoInviteResult>>('/api/v1/user/getInviteUsers')
    return res.content
}

/**
 * 获取邀请码是否有效
 * @return {*}
 */
export async function invitationCodeCheck(invitationCode: string): Promise<boolean> {
    const res = await http.get<unknown, HttpResp<boolean>>('/api/auth/v1/user/invitationCodeCheck', {
        params: { invitationCode }
    })
    return res.content
}

/**
 * 用户完成任务
 * @return {*}
 */
export async function taskCompleted(userTask: string[]): Promise<boolean> {
    const res = await http.post<unknown, HttpResp<boolean>>('/api/v1/user/taskCompleted', userTask, {
        headers: JsonContentType
    })
    return res.success
}
